import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import GlobalAppBar from './GlobalAppBar';
import BottomNavigation from './BottomNavigation';
import useNotifications from './useNotifications';
import NotificationToast from './NotificationToast';
import { appConfig } from '../config';
import './MyCourses.css';

// HELPER: Wählt den Inhalt basierend auf der aktuellen Sprache
const getLocalizedContent = (item, field, langCode) => {
  if (!item) return '';
  const code = langCode ? String(langCode).split('-')[0].toLowerCase() : 'de';
  const localizedField = `${field}_${code}`;
  
  if (item[localizedField] && String(item[localizedField]).trim() !== '') {
    return item[localizedField];
  }
  return item[field] || '';
};

const MyCourses = () => {
  const { t, currentLanguage } = useLanguage(); 
  
  const navigate = useNavigate();
  const location = useLocation();
  const { user, getMyCourses, getFavourites, searchByTerm, toggleFavourites, setProgress, makeAuthenticatedRequest } = useAuth();
  const { notifications, dismissOne, dismissAll } = useNotifications();
  
  const [coursesData, setCoursesData] = useState([]);
  const [allCoursesData, setAllCoursesData] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [isSearchActive, setIsSearchActive] = useState(false);
  
  const [isTwoColumnLayout, setIsTwoColumnLayout] = useState(true); 
  
  // --- ADMIN FILTER STATES ---
  // Default auf 'active' geändert, damit standardmäßig nur sichtbare Elemente gezeigt werden
  const [statusFilter, setStatusFilter] = useState('active');
  const [showOnlyMyContent, setShowOnlyMyContent] = useState(false);

  useEffect(() => {
    const savedLayout = localStorage.getItem('hejvi_display_layout');
    if (savedLayout !== null) {
      setIsTwoColumnLayout(savedLayout === 'two-column');
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('hejvi_display_layout', isTwoColumnLayout ? 'two-column' : 'single-column');
  }, [isTwoColumnLayout]);

  const normalizeTags = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) {
      return raw
        .map((item) => {
          if (item && typeof item === 'object') {
            return getLocalizedContent(item, 'name', currentLanguage).trim();
          }
          return String(item || '').trim();
        })
        .filter(Boolean);
    }
    return String(raw).trim() ? [String(raw).trim()] : [];
  };

  useEffect(() => {
    fetchCoursesData();
  }, [currentLanguage]);

  useEffect(() => {
    if (location.state?.searchContext) {
      const { query, active, tags } = location.state.searchContext;
      if (tags) setSelectedTags(tags);
      if (query) setSearchQuery(query);
      if (active) setIsSearchActive(true);

      if (query && active) {
        setTimeout(() => {
          performSearch(query);
        }, 50);
      }
    }
  }, [location.state]);

  // --- HAUPT FILTER LOGIK (Lokal) ---
  // FIX: searchQuery muss hier dabei sein, damit Filtern beim Tippen geht!
  useEffect(() => {
    if (allCoursesData.length > 0) {
      filterCourses();
    }
  }, [allCoursesData, selectedTags, statusFilter, showOnlyMyContent, searchQuery]); 

  const fetchCoursesData = async () => {
    try {
      setLoading(true);
      
      // 1. Daten holen (Kurse & Favoriten)
      const [dataResponse, favResponse] = await Promise.all([
        getMyCourses(),
        (async () => { try { return await getFavourites(); } catch (_) { return null; } })()
      ]);

      const responseUserId = dataResponse?.userid;

      // ... (Dein Code zum Erstellen von favouriteSet bleibt gleich) ...
      const favouriteSet = (() => {
        const set = new Set();
        const favPayload = favResponse;
        if (!favPayload) return set;
        const collect = (arr) => (Array.isArray(arr) ? arr : []).forEach((it) => it?.hash_id && set.add(it.hash_id));
        if (favPayload?.status === 'success') {
          if (Array.isArray(favPayload?.data)) collect(favPayload.data);
          if (favPayload?.data?.elements) collect(favPayload.data.elements);
          if (favPayload?.data?.collections) collect(favPayload.data.collections);
        } else if (Array.isArray(favPayload)) {
          collect(favPayload);
        }
        return set;
      })();
      
      // ... (Dein Code zum Zusammenführen von coursesList bleibt gleich) ...
      let coursesList = [];
      if (dataResponse?.status === 'success' && dataResponse?.data) {
          // ... (Liste flachklopfen wie bisher)
          if (Array.isArray(dataResponse.data)) {
            coursesList = dataResponse.data;
          } else {
            const { collections, elements } = dataResponse.data;
            if (Array.isArray(collections)) coursesList.push(...collections);
            if (Array.isArray(elements)) coursesList.push(...elements);
          }
      } else if (Array.isArray(dataResponse?.data)) {
         coursesList.push(...dataResponse.data);
      }

      // Duplikate entfernen (bleibt gleich)
      const uniqueByHash = new Map();
      for (const item of coursesList) {
        if (item?.hash_id && !uniqueByHash.has(item.hash_id)) {
          uniqueByHash.set(item.hash_id, item);
        }
      }
      coursesList = Array.from(uniqueByHash.values());
      
      // Datentransformation (bleibt gleich, hier werden is_author und favourite_value gesetzt)
      const transformedCourses = coursesList.map(course => ({
        ...course, 
        hash_id: course.hash_id,
        id: course.id,
        title: getLocalizedContent(course, 'name', currentLanguage) || course.title || course.name,
        // ...
        // WICHTIG: Diese Felder nutzen wir gleich zum Filtern:
        is_author: responseUserId && course.author ? String(course.author) === String(responseUserId) : false,
        favourite_value: favouriteSet.has(course.hash_id) ? 1 : 0,
        // ...
        tags: normalizeTags(course.tags),
      }));
      
      // -------------------------------------------------------------
      // NEU: FILTER FÜR USER GRUPPE 1 (Nur Eigene & Favoriten)
      // -------------------------------------------------------------
      let finalCourses = transformedCourses;

      if (user && parseInt(user.group) === 1) {
          finalCourses = transformedCourses.filter(item => {
              // Behalten, wenn:
              // 1. Man selbst der Autor ist
              // 2. ODER man es zu den Favoriten hinzugefügt hat
              return item.is_author === true || item.favourite_value === 1;
          });
      }
      // Gruppe 0 und 2 sehen weiterhin alles (finalCourses = transformedCourses)
      // -------------------------------------------------------------

      // Tags extrahieren (basierend auf der gefilterten Liste!)
      const tagSet = new Set();
      finalCourses.forEach(course => {
        if (course.tags && Array.isArray(course.tags)) {
          course.tags.forEach(tag => {
            if (tag && typeof tag === 'string' && tag.trim()) tagSet.add(tag.trim());
          });
        }
      });
      setAvailableTags(Array.from(tagSet).sort());
      
      // Sortieren (mit der gefilterten Liste)
      const sortedCourses = finalCourses.sort((a, b) => {
        const dateA = new Date(a.created || 0);
        const dateB = new Date(b.created || 0);
        return dateB - dateA;
      });

      // State setzen
      setAllCoursesData(sortedCourses);
      setCoursesData(sortedCourses);
      setFilteredCourses(sortedCourses);

    } catch (error) {
      console.error('Failed to fetch courses data:', error);
      setCoursesData([]);
      setAllCoursesData([]);
    } finally {
      setLoading(false);
    }
  };

  const filterCourses = (customList = null) => {
    // Wenn eine customList übergeben wird (z.B. Suchergebnisse), nutzen wir die als Basis
    let filtered = customList ? [...customList] : [...allCoursesData];

    // 1. Text Search (nur wenn wir lokal filtern, also keine customList haben)
    if (!customList && searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(course =>
        (course.title && course.title.toLowerCase().includes(query)) ||
        (course.description && course.description.toLowerCase().includes(query))
      );
    }

    // 2. Tag Filter
    if (selectedTags.length > 0) {
      filtered = filtered.filter(course => {
        const courseTags = Array.isArray(course.tags) ? course.tags : [];
        return selectedTags.some(tag => courseTags.includes(tag));
      });
    }

    // 3. Status & Author Filter (Admin Logic)
    const isAdmin = user && (parseInt(user.group) === 0 || parseInt(user.group) === 2);

    if (!isAdmin) {
       filtered = filtered.filter(c => c.status === 1 || c.is_author);
    } else {
       if (statusFilter === 'active') {
           filtered = filtered.filter(c => c.status === 1);
       } else if (statusFilter === 'inactive') {
           filtered = filtered.filter(c => c.status === 0);
       }
       if (showOnlyMyContent) {
           filtered = filtered.filter(c => c.is_author);
       }
    }

    setFilteredCourses(filtered);
  };

  // --- API SUCHE ---
  const performSearch = async (query) => {
     if (!query.trim()) { filterCourses(); return; }
     try {
        setLoading(true);
        const searchResults = await searchByTerm(query, { limit: 50 });
        
        // FIX: Ergebnisse wirklich verarbeiten statt ignorieren!
        if (searchResults?.status === 'success' && searchResults?.data) {
            
            // Mapping der Suchergebnisse auf das App-Format
            const responseUserId = user?.id || user?.userid; // Fallback
            
            const mappedResults = searchResults.data.map(item => {
                // Prüfen ob wir das Item schon lokal haben (um Progress/Fav zu behalten)
                const localMatch = allCoursesData.find(c => c.hash_id === item.hash_id);
                if (localMatch) return localMatch; 

                // Wenn neu: Mapping anwenden
                return {
                    hash_id: item.hash_id,
                    id: item.id,
                    title: getLocalizedContent(item, 'name', currentLanguage) || item.title || item.name,
                    name: getLocalizedContent(item, 'name', currentLanguage) || item.title || item.name,
                    description: getLocalizedContent(item, 'description', currentLanguage),
                    url_element: item.url_element || item.url || '', 
                    image: item.url_thumbnail || item.image || 'https://app.hejvi.de/api/img/thumb_metal.jpg',
                    url_thumbnail: item.url_thumbnail || item.image,
                    duration: item.duration ? `${Math.floor(item.duration / 60)} min` : '0 min',
                    progress: 0, 
                    is_mandatory: Number(item.is_mandatory) === 1,
                    // WICHTIG: Admin Logik auch hier anwenden
                    is_author: responseUserId && item.author ? String(item.author) === String(responseUserId) : false,
                    status: item.status !== undefined ? parseInt(item.status) : 1,
                    favourite_value: 0,
                    type: item.type,
                    tags: normalizeTags(item.tags)
                };
            });

            // Wir nutzen filterCourses mit der customList, damit Tags/Admin-Filter auch auf Suchergebnisse wirken
            filterCourses(mappedResults);

        } else {
            filterCourses();
        }
     } catch(e) {
         console.error(e);
         filterCourses();
     } finally {
         setLoading(false);
     }
  };
  
  const handleSearchToggle = () => { setIsSearchActive(!isSearchActive); if (isSearchActive) { setSearchQuery(''); setFilteredCourses(allCoursesData); } };
  const handleSearchQuery = (query) => { setSearchQuery(query); };
  const handleSearchSubmit = async (query) => { if (!query.trim()) { filterCourses(); return; } await performSearch(query); };
  const handleTagToggle = (tag) => { setSelectedTags(prev => { return prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]; }); };
  const toggleFavorite = async (elementId) => { 
      try {
        const updateList = (list) => list.map(item => item.hash_id === elementId ? { ...item, favourite_value: item.favourite_value ? 0 : 1 } : item);
        setFilteredCourses(prev => updateList(prev));
        setAllCoursesData(prev => updateList(prev));
        await toggleFavourites(elementId);
      } catch(e) { console.error(e); }
  };
  const handleDisplayToggle = () => { setIsTwoColumnLayout(!isTwoColumnLayout); };
  
  const handleDeleteItem = async (e, item) => {
    e.stopPropagation();
    if (!window.confirm(t('confirmDelete'))) return;

    const hashId = item.hash_id || item.hashId;
    const isCollection = hashId.startsWith('col-');
    const endpoint = isCollection ? '/delete-course' : '/delete-element';

    try {
      const res = await makeAuthenticatedRequest(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hash_id: hashId })
      });
      const result = await res.json();

      if (result.status === 'success') {
        const removeFilter = (list) => list.filter(i => (i.hash_id || i.hashId) !== hashId);
        setAllCoursesData(prev => removeFilter(prev));
        setFilteredCourses(prev => removeFilter(prev));
      } else {
        alert(result.message || t('deleteError'));
      }
    } catch (error) {
      console.error("Delete failed:", error);
      alert(t('deleteError'));
    }
  };

  const handleToggleVisibility = async (e, item) => {
      e.stopPropagation();
      const hashId = item.hash_id || item.hashId;
      
      try {
          const res = await makeAuthenticatedRequest('/toggle-visibility', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ hash_id: hashId })
          });
          const result = await res.json();

          if (result.status === 'success') {
              const newStatus = result.new_status;
              const updateList = (list) => list.map(c => {
                  if ((c.hash_id || c.hashId) === hashId) {
                      return { ...c, status: newStatus };
                  }
                  return c;
              });
              // Update beides: Original Daten und aktuelle Ansicht
              setAllCoursesData(prev => updateList(prev));
              setFilteredCourses(prev => updateList(prev));
          } else {
              alert(result.message || 'Error updating visibility');
          }
      } catch (err) {
          console.error("Visibility toggle failed", err);
      }
  };

  if (loading) return <div className="dashboard-container"><div className="loading-state"><div className="spinner"></div><p>{t('loadingCourses')}</p></div></div>;

  const isAdminOrManager = user && (parseInt(user.group) === 0 || parseInt(user.group) === 2);

  return (
    <div className="dashboard-container">
      <GlobalAppBar 
        title={t('myCourses')}
        showSearch={true}
        showDisplayToggle={true}
        isTwoColumnLayout={isTwoColumnLayout}
        onDisplayToggle={handleDisplayToggle}
        onSearchToggle={handleSearchToggle}
        onSearchQuery={handleSearchQuery}
        onSearchSubmit={handleSearchSubmit}
        searchQuery={searchQuery}
        isSearchActive={isSearchActive}
      />
      
      {/* FILTER AREA */}
      <div className="tags-container" style={{
          display:'flex',
          flexDirection: 'column',
          gap:'10px', 
          padding: '10px 20px', 
          position: 'relative', 
          zIndex: 1000, 
          background: '#fff', 
          borderBottom: '1px solid #eee',
          marginTop: '80px' 
      }}>

        {/* VOLLTEXT-SUCHFELD */}
        <div className="mc-search">
          <svg className="mc-search__icon" width="16" height="16" viewBox="0 0 20 20" fill="none">
            <circle cx="9" cy="9" r="7" stroke="currentColor" strokeWidth="2"/>
            <path d="M14.5 14.5L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <input
            type="text"
            value={searchQuery}
            onChange={e => handleSearchQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearchSubmit(searchQuery)}
            placeholder={t('searchPlaceholder') || 'Inhalte durchsuchen…'}
            className="mc-search__input"
          />
          {searchQuery && (
            <button
              className="mc-search__clear"
              onClick={() => { handleSearchQuery(''); setFilteredCourses(allCoursesData); }}
              aria-label="Suche löschen"
            >×</button>
          )}
        </div>

        {/* ADMIN FILTERS + TAGS */}
        <div style={{display:'flex', gap:'10px', alignItems:'center'}}>

        {isAdminOrManager && (
            <div style={{display:'flex', gap:'8px', marginRight:'10px', alignItems:'center'}}>
                <select 
                    value={statusFilter} 
                    onChange={(e) => setStatusFilter(e.target.value)}
                    style={{
                        padding: '8px 12px', borderRadius: '20px', border: '1px solid #ddd',
                        background: '#f9f9f9', fontSize: '13px', fontWeight: '600', color: '#333', cursor: 'pointer'
                    }}
                >
                    <option value="all">{t('all') || 'All'}</option>
                    <option value="active">{t('active') || 'Active'} (Status 1)</option>
                    <option value="inactive">{t('hidden') || 'Hidden'} (Status 0)</option>
                </select>

                <button
                    onClick={() => setShowOnlyMyContent(!showOnlyMyContent)}
                    title={t('filterMyContent') || "Only my content"}
                    style={{
                        background: showOnlyMyContent ? '#FF6407' : '#fff',
                        color: showOnlyMyContent ? '#fff' : '#333',
                        border: showOnlyMyContent ? '1px solid #FF6407' : '1px solid #ddd', 
                        borderRadius: '20px', padding: '8px 14px', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: '6px', transition: 'all 0.2s'
                    }}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                    {t('filterMyContent') || "Meine Inhalte"}
                </button>
            </div>
        )}

        {availableTags.length > 0 && (
          <div className="tags-scroll" style={{flex: 1, overflowX: 'auto', display: 'flex', gap: '8px'}}>
            <button key="__all__" className={`tag-button ${selectedTags.length === 0 ? 'active' : ''}`} onClick={() => setSelectedTags([])}>#all</button>
            {availableTags.map((tag) => (
              <button key={tag} className={`tag-button ${selectedTags.includes(tag) ? 'active' : ''}`} onClick={() => handleTagToggle(tag)}>#{tag}</button>
            ))}
          </div>
        )}
        </div>{/* Ende inner filter row */}
      </div>

      <div className="feed-container">
        {filteredCourses.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
            <p>{t('noResults')}</p>
          </div>
        ) : (
          <div className={`feed-grid ${isTwoColumnLayout ? 'feed-grid-two-column' : 'feed-grid-single-column'}`}>
            {filteredCourses.map((item) => {
              const hashId = item?.hash_id ?? item?.hashId ?? null;
              const routeId = hashId || item?.id || 'local';
              
              const canManage = isAdminOrManager || item.is_author;
              const isHidden = item.status === 0;

              if (isHidden && !canManage) return null;

              let image = item?.url_thumbnail || item?.image; // Kein Standard-Bild mehr
              if (image && image.includes('app.hejvi.de/img/') && !image.includes('/api/img/')) {
                image = image.replace('app.hejvi.de/img/', 'app.hejvi.de/api/img/');
              }
              const title = item?.title || item?.name;
              
              return (
                  <div 
                    key={hashId || 'unknown-' + Math.random()}
                    className="feed-card"
                    style={isHidden ? { opacity: 0.6, border: '1px dashed #999', filter: 'grayscale(100%)' } : {}}
                    onClick={async (e) => {
                      if (e.target.closest('.feed-fav') || e.target.closest('.feed-play') || e.target.closest('.feed-badges-container')) return;
                      if (!hashId) return;
                      if (parseFloat(item?.progress) === 0) { try { await setProgress(hashId, 1); } catch (error) {} }
                      const searchContext = { query: searchQuery, active: isSearchActive, tags: selectedTags };
                      if (hashId.startsWith('el-')) {
                           if (appConfig?.workflow?.directVideoPlay) {
                                navigate(`/element-feed/${hashId}`, { state: { lessons: [item], startIndex: 0, collectionData: null, sourcePage: '/my-courses', searchContext } });
                           } else {
                                navigate(`/element/${hashId}`, { state: { elementData: item, sourcePage: '/my-courses', searchContext } });
                           }
                      } else if (hashId.startsWith('col-')) {
                           navigate(`/course/${routeId}`, { state: { collectionData: item, sourcePage: '/my-courses', searchContext } });
                      }
                    }}
                  >
                  <div className="feed-media" style={{ 
                      backgroundImage: image ? `url(${image})` : 'none',
                      backgroundColor: image ? 'transparent' : 'rgba(255,255,255,0.05)' // Dezent grauer Platzhalter
                  }}> 
                    <div className="feed-badges-container" style={{ position: 'absolute', top: '5px', left: '5px', display: 'flex', flexDirection: 'column', gap: '6px', zIndex: 3 }}>
                      
                      {item?.is_mandatory && (
                        <div className="feed-badge" style={{ position:'relative', top:'0', left:'0', background: 'rgba(67, 67, 67, 0.9)' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><path d="M7 12.5L10.2 15.7L17 9"></path></svg>
                          <span style={{fontSize:'10px'}}>{t('mandatory')}</span>
                        </div>
                      )}

                      {canManage && (
                        <div style={{display:'flex', gap:'5px'}}>
                            <button 
                                className="feed-action-btn"
                                onClick={(e) => handleDeleteItem(e, item)}
                                title={t('delete')}
                                style={{ background: 'rgba(220, 38, 38, 0.9)', border:'none', borderRadius:'50%', width:'26px', height:'26px', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', boxShadow:'0 2px 4px rgba(0,0,0,0.3)' }}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                            </button>

                            <button 
                                className="feed-action-btn"
                                onClick={(e) => handleToggleVisibility(e, item)}
                                title={isHidden ? t('makeVisible') : t('makeInvisible')}
                                style={{ 
                                    background: isHidden ? 'rgba(100, 100, 100, 0.9)' : 'rgba(16, 185, 129, 0.9)', 
                                    border:'none', borderRadius:'50%', width:'26px', height:'26px', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', boxShadow:'0 2px 4px rgba(0,0,0,0.3)' 
                                }}
                            >
                                {isHidden ? (
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                                ) : (
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                                )}
                            </button>
                        </div>
                      )}
                    </div>

                    <button className="feed-play" onClick={async (e) => {
                        e.stopPropagation();
                        if(!hashId) return;
                        const searchContext = { query: searchQuery, active: isSearchActive, tags: selectedTags };
                        if (hashId.startsWith('el-')) {
                             if (appConfig?.workflow?.directVideoPlay) {
                                navigate(`/element-feed/${hashId}`, { state: { lessons: [item], startIndex: 0, collectionData: null, sourcePage: '/my-courses', searchContext } });
                             } else {
                                navigate(`/element/${hashId}`, { state: { elementData: item, sourcePage: '/my-courses', searchContext } });
                             }
                        }
                        else if (hashId.startsWith('col-')) navigate(`/course/${routeId}`, { state: { collectionData: item, sourcePage: '/my-courses', searchContext } });
                    }}>
                      <svg width="80" height="80" viewBox="0 0 80 80" fill="none"><circle cx="40" cy="40" r="40" fill="rgba(255,255,255,0.95)"/><path d="M32 25L55 40L32 55V25Z" fill="#FF6407"/></svg>
                    </button>

                    <button className={`feed-fav ${Number(item?.favourite_value) === 1 ? 'active' : ''}`} onClick={(e) => { e.stopPropagation(); hashId && toggleFavorite(hashId); }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M12 17.3L6.5 20.5L7.9 14.2L3 9.9L9.4 9L12 3L14.6 9L21 9.9L16.1 14.2L17.5 20.5L12 17.3Z" fill={Number(item?.favourite_value) === 1 ? '#FF6407' : '#A3A3A3'} /></svg>
                    </button>

                    <div className="feed-footer">
                      {title && <h3 className="feed-title">{title} {isHidden && <small style={{color:'#D1D5DB', marginLeft:'5px'}}>(Versteckt)</small>}</h3>}
                      {parseFloat(item?.progress) > 0 && <div className="feed-progress">
                        <div className="feed-progress-fill" style={{ width: `${item.progress}%` }}></div></div>}
                      {/* --- NEU: Badge für Inhaltstyp (V, Q, K) --- */}
                    {(() => {
                      let badgeChar = '';
                      let badgeColor = '';
                      
                      if (hashId && hashId.startsWith('col-')) {
                        badgeChar = 'K'; // Kurs
                        badgeColor = 'rgba(37, 99, 235, 0.8)'; // Blau
                      } else if (Number(item.type) === 3) {
                        badgeChar = 'Q'; // Quiz
                        badgeColor = 'rgba(245, 158, 11, 0.8)'; // Orange
                      } else {
                        badgeChar = 'V'; // Video (Standard)
                        badgeColor = 'rgba(16, 185, 129, 0.8)'; // Grün
                      }

                      return (
                        <div style={{
                          position: 'absolute',
                          bottom: 0,
                          right: 0,
                          backgroundColor: badgeColor,
                          color: '#fff',
                          padding: '2px 10px',
                          borderTopLeftRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '800',
                          zIndex: 5,
                          boxShadow: '-2px -2px 4px rgba(0,0,0,0.2)'
                        }}>
                          {badgeChar}
                        </div>
                      );
                    })()}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <NotificationToast
        notifications={notifications}
        onDismiss={dismissOne}
        onDismissAll={dismissAll}
        onNavigate={(path) => navigate(path)}
      />
      <BottomNavigation />
    </div>
  );
};

export default MyCourses;